<template>
  <div
    :class="[
      'coupon',
      {
        'coupon-code--closed': !appliedCoupon,
        'coupon-code--opened': appliedCoupon,
        apply: appliedCoupon,
      },
    ]"
  >
    <div v-if="hasCouponApplied()" class="coupon-code__applied-section">
      <div class="coupon-code--removable">
        <span class="coupon-code__icon">
          <icon-ticket :size="18" />
        </span>
        {{ appliedPromotion.code }}
      </div>
      <div class="coupon-code__action">
        <a
          v-if="!calculating"
          class="coupon-code__removal"
          rel="nofollow"
          @click.prevent="removeCoupon"
        >
          {{ $t('cart.coupon_code.remove_discount') }}
          <icon-trash :size="18" />
        </a>
        <three-dots-loading v-else />
      </div>
    </div>
    <form v-else class="coupon-code" @submit="applyCoupon">
      <a
        v-if="!isNewCart"
        class="coupon-code__toggle"
        rel="nofollow"
        @click.prevent="couponToggle"
      >
        {{ $t('cart.coupon_code.apply_discount') }}
      </a>
      <div class="coupon-code__fields-apply">
        <input
          v-model="couponCode"
          type="text"
          name="coupon_code"
          class="coupon-code__input"
          :placeholder="$t('cart.coupon_code.title')"
        />
        <v-button
          type="submit"
          :label="$t('cart.coupon_code.apply')"
          class="coupon-code__btn-apply"
          height="44px"
          :loading="calculating"
        />
      </div>
    </form>
  </div>
</template>

<script>
import appendUrlParam from '~/utils/appendUrlParam'
import VButton from '~/components/v-button/VButton.vue'
import IconTrash from '~/components/icons/trash/Trash'
import IconTicket from '~/components/icons/ticket/Ticket'
import ThreeDotsLoading from '~/components/loadings/three-dots/ThreeDotsLoading.vue'

export default {
  name: 'CouponCode',
  components: {
    VButton,
    IconTrash,
    IconTicket,
    ThreeDotsLoading,
  },
  props: {
    isNewCart: {
      type: Boolean,
      default: false,
    },
    appliedPromotion: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      appliedCoupon: this.isNewCart,
      couponCode: '',
      calculating: false,
    }
  },
  methods: {
    hasCouponApplied() {
      return this.appliedPromotion && Object.keys(this.appliedPromotion).length
    },
    couponToggle() {
      this.appliedCoupon = !this.appliedCoupon
    },

    async applyCoupon(e) {
      if (this.calculating) return
      e.preventDefault()
      if (!this.couponCode) {
        this.$displayTopMessage(this.$t('cart.coupon_code.empty'), {
          type: 'error',
        })
        return
      }
      this.$store.dispatch?.('analytics/emitGtagEvent', {
        name: 'apply_coupon',
        params: { coupon: this.couponCode },
      })
      await this.makeCouponRequest(
        this.couponCode,
        'coupons/apply',
        'cart/handleCouponAproved'
      )
    },

    async removeCoupon(e) {
      if (this.calculating) return
      e.preventDefault()

      await this.makeCouponRequest(
        this.appliedPromotion.code,
        'coupons/remove',
        'cart/handleCouponRemoved'
      )
    },
    async makeCouponRequest(couponCode, path, storeDispatch) {
      this.calculating = true

      const params = {
        ...this.$store.getters.storeInfo,
        coupon_code: couponCode,
      }

      try {
        const result = await this.$apiv2.$post(appendUrlParam(path, params))
        this.$displayTopMessage(result.message)
        this.$emit('updateCart', result.cart)
        this.$store.dispatch(storeDispatch, true)
      } catch (err) {
        const errorMessage = err?.response?.data?.message
        let customErrorMessage
        if (this.$store.state.isBlackFriday) {
          const errorMessagesMap = {
            "This coupon code can't be applied to orders less than or equal to R$99,00.": this.$t(
              'cart.coupon_code.black_friday_minimum_error'
            ),
            'O cupom não é válido para esse pedido': this.$t(
              'cart.coupon_code.black_friday_invalid_order'
            ),
          }
          customErrorMessage = errorMessagesMap[errorMessage] || errorMessage
        } else {
          customErrorMessage = errorMessage
        }
        this.$displayTopMessage(customErrorMessage, {
          type: 'error',
        })
      } finally {
        this.calculating = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.coupon {
  width: 100%;
  @media (max-width: $v4_tablet) {
    margin-left: -15px;
    margin-right: -15px;
    width: auto;
  }
}
.coupon-code {
  width: 100%;
  box-sizing: border-box;
  @media (max-width: $v4_tablet) {
    padding: 0 15px;
  }
}

.coupon-code__toggle {
  display: block;
  margin: 0;
  font-size: 15px;
  line-height: 18px;
  color: #555;
  text-decoration: none;
  text-align: right;

  &:hover {
    color: #a2a2a6;
  }

  &::before {
    content: '+';
    margin-right: 2px;
  }
}
.coupon-code__toggle .icon {
  margin-right: 2px;
}

.coupon-code--closed {
  & .coupon-code__fields-apply {
    display: none;
  }
  & .coupon-code__fields-remove {
    display: none;
  }
  & .close {
    display: none;
  }
  & .open {
    display: inline-block;
  }

  .coupon-code__toggle::before {
    content: '+';
  }
}
.coupon-code--opened {
  & .coupon-code__fields-apply {
    display: flex;
    margin-top: $v4_gutter;
  }
  & .coupon-code__fields-remove {
    display: none;
  }
  & .close {
    display: inline-block;
  }
  & .open {
    display: none;
  }

  .coupon-code__toggle::before {
    content: '-';
  }
}

.coupon-code__applied-section {
  display: grid;
  grid-template-columns: 8fr 4fr;
  gap: 0.5em;
}

.coupon-code__icon {
  padding: 5px 10px 0px 0px;
}

.coupon-code__description {
  font-size: 11px;
}
.coupon-code--removable {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #e2e2e6;
  border-radius: 3px;
  font-weight: bold;
  text-align: center;
  padding: 5px 15px;
  margin: 0px 5px;
}

.coupon-code__action {
  display: flex;
  align-items: center;
  justify-content: center;
}

.coupon-code__removal {
  display: flex;
  align-items: center;
  padding: 5px 10px;
  text-decoration: underline;
  text-transform: lowercase;
  color: #a2a2a6;

  svg {
    fill: #a2a2a6;
    margin: 2px 0px 0px 5px;
  }
}

.coupon-code__removal:hover {
  color: #333;
  svg {
    fill: #333;
  }
}

.coupon-code {
  @media (max-width: $v4_tablet) {
    margin-top: $v4_gutter;
  }
}

.coupon-code__fields {
  margin-top: 10px;
  text-align: left;
  display: flex;
}
.coupon-code__input[type='text'] {
  line-height: normal;
  width: 100%;
  font-size: 13px;
  border-radius: 0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.coupon-code__btn-apply {
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

@media (min-width: $screen_large) {
  .coupon-code {
    margin-top: 0;
  }
}
</style>
